import { useMemo, useState } from "react";
import { Loader2, Scale, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Avatar, ShareStatus } from "@/components/common";
import { AllocationBar, ContributionTable } from "@/components/plan/ContributionTable";
import { api, apiError, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

export function initialDraft(exp) {
  const p = exp.plan;
  return {
    budget_total: p.budget_total ?? "", currency: p.currency, contribution_mode: p.contribution_mode,
    contributions: Object.fromEntries(p.entries.map((e) => [e.user.id, e.value])),
    guest_terms: { covered: "", excluded: "", undecided: "", ...exp.guest_terms }, guest_places: exp.guest_places,
  };
}

export function draftBody(d) {
  return { ...d, budget_total: d.budget_total === "" ? null : Number(d.budget_total), guest_places: Number(d.guest_places) || 0,
    contributions: Object.fromEntries(Object.entries(d.contributions).map(([k, v]) => [k, Number(v) || 0])) };
}

const ModeToggle = ({ value, onChange }) => (
  <div className="inline-flex rounded-[10px] border border-border p-1" role="radiogroup" aria-label="Contribution type">
    {[["percent", "Percentages"], ["amount", "Fixed amounts"]].map(([k, l]) => (
      <button key={k} type="button" role="radio" aria-checked={value === k} onClick={() => onChange(k)} data-testid={`mode-${k}`}
        className={cn("min-h-[40px] rounded-md px-4 text-sm transition-colors", value === k ? "bg-primary text-primary-foreground" : "text-foreground/80 hover:text-foreground")}>{l}</button>
    ))}
  </div>
);

const ContributionEditor = ({ exp, draft, setDraft }) => {
  const budget = Number(draft.budget_total) || 0;
  const pct = draft.contribution_mode === "percent";
  const entries = useMemo(() => exp.plan.entries.map((e) => {
    const v = Number(draft.contributions[e.user.id]) || 0;
    return { ...e, percent: pct ? v : budget ? Math.round((v / budget) * 10000) / 100 : 0 };
  }), [exp.plan.entries, draft, pct, budget]);
  const allocated = Math.round(entries.reduce((s, e) => s + e.percent, 0) * 100) / 100;
  const setVal = (uid, v) => setDraft((d) => ({ ...d, contributions: { ...d.contributions, [uid]: v } }));

  const splitEqually = () => {
    const n = entries.length;
    const total = pct ? 100 : budget;
    if (!total) return toast.error("Set a total budget first");
    const base = Math.floor((total / n) * 100) / 100;
    const vals = entries.map((_, i) => (i === 0 ? Math.round((total - base * (n - 1)) * 100) / 100 : base));
    setDraft((d) => ({ ...d, contributions: Object.fromEntries(entries.map((e, i) => [e.user.id, vals[i]])) }));
  };

  return (
    <div className="space-y-6" data-testid="contribution-editor">
      <div className="grid gap-4 sm:grid-cols-[1fr_140px]">
        <div>
          <label htmlFor="budget" className="field-label">Total budget <span className="font-normal text-muted-foreground">{pct ? "(optional with percentages)" : "(required for amounts)"}</span></label>
          <input id="budget" type="number" min={0} step="100" className="field" value={draft.budget_total} onChange={(e) => setDraft((d) => ({ ...d, budget_total: e.target.value }))} placeholder="100000" data-testid="plan-budget-input" readOnly={!!exp.cost_items?.length} />
          {exp.cost_items?.length > 0 && <p className="mt-1 text-xs text-muted-foreground" data-testid="plan-budget-from-calculator">Comes from the cost calculator. Edit the cost lines on the experience to change it.</p>}
        </div>
        <div>
          <label htmlFor="currency" className="field-label">Currency</label>
          <select id="currency" className="field [&>option]:bg-card" value={draft.currency} onChange={(e) => setDraft((d) => ({ ...d, currency: e.target.value }))} data-testid="plan-currency-select">
            {["EUR", "USD", "GBP", "CHF", "AED"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <ModeToggle value={draft.contribution_mode} onChange={(m) => setDraft((d) => ({ ...d, contribution_mode: m, contributions: Object.fromEntries(Object.keys(d.contributions).map((k) => [k, 0])) }))} />
        <button type="button" className="btn-outline min-h-[40px] px-4 text-sm" onClick={splitEqually} data-testid="split-equally-button"><Scale className="h-4 w-4" /> Split equally</button>
      </div>
      <ul className="divide-y divide-border">
        {entries.map((e) => (
          <li key={e.user.id} className="flex flex-wrap items-center gap-4 py-4">
            <Avatar person={e.user} size={48} className="rounded-lg" />
            <div className="min-w-[120px] flex-1"><p className="font-display text-xl">{e.user.name}</p><p className="text-xs text-muted-foreground">{e.role === "lead" ? "Lead host" : "Co-host"}</p></div>
            <div className="flex items-center gap-2">
              <input type="number" min={0} step={pct ? "0.5" : "100"} aria-label={`${e.user.name} contribution`} className="field w-[130px] text-right" value={draft.contributions[e.user.id] ?? 0}
                onChange={(ev) => setVal(e.user.id, ev.target.value)} data-testid={`contribution-input-${e.user.id}`} />
              <span className="w-10 text-sm text-muted-foreground">{pct ? "%" : draft.currency}</span>
            </div>
            <p className="w-28 text-right text-sm text-muted-foreground">{pct ? (budget ? money((budget * e.percent) / 100, draft.currency) : "") : `${e.percent}%`}</p>
          </li>
        ))}
      </ul>
      <AllocationBar entries={entries} />
      <p className={cn("text-lg", allocated > 100.01 ? "text-destructive" : Math.abs(allocated - 100) < 0.01 ? "text-primary" : "")} data-testid="editor-allocation-summary">
        {allocated}% allocated{allocated > 100.01 ? " · over-allocated" : Math.abs(allocated - 100) < 0.01 ? "" : ` · ${Math.round((100 - allocated) * 100) / 100}% remaining`}
      </p>
    </div>
  );
};

const MyResponse = ({ exp, onUpdate }) => {
  const { user } = useAuth();
  const [busy, setBusy] = useState("");
  const me = exp.plan.entries.find((e) => e.user.id === user.id);
  if (exp.plan.plan_state !== "sent" || !me) return null;
  const respond = async (accept) => {
    setBusy(accept ? "a" : "d");
    try {
      const { data } = await api.post(`/experiences/${exp.id}/plan/respond`, { version: exp.plan.plan_version, accept });
      toast.success(accept ? "Your contribution is confirmed" : "You declined the proposed contribution");
      onUpdate(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  return (
    <div className="rounded-xl border border-primary/50 bg-primary/5 p-5" data-testid="my-contribution-response">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p>Your proposed share (v{exp.plan.plan_version}): <strong className="font-display text-2xl">{me.percent != null ? `${me.percent}%` : ""}</strong>{me.amount != null && <span className="text-muted-foreground"> · {money(me.amount, exp.plan.currency)}</span>}</p>
        <ShareStatus status={me.status} testId="my-share-status" />
      </div>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <button type="button" className="btn-primary" disabled={!!busy || me.status === "accepted"} onClick={() => respond(true)} data-testid="accept-contribution-button">{busy === "a" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accept my contribution"}</button>
        <button type="button" className="btn-outline" disabled={!!busy || me.status === "declined"} onClick={() => respond(false)} data-testid="decline-contribution-button">{busy === "d" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Decline"}</button>
      </div>
    </div>
  );
};

export const ContributionsStep = ({ exp, draft, setDraft, onUpdate }) => {
  const lead = exp.my_role === "lead";
  const sent = exp.plan.plan_state === "sent";
  return (
    <div className="space-y-8">
      <div>
        <h2 className="font-display text-4xl leading-tight sm:text-5xl">Agree the contributions</h2>
        <p className="mt-2 text-foreground/85">Each co-host can contribute a different amount.</p>
      </div>
      {exp.plan.agreed_version && exp.status !== "agreed" && (
        <p className="flex items-start gap-3 rounded-lg border border-[hsl(var(--gold))]/50 p-4 text-sm" data-testid="terms-changed-banner">
          <AlertTriangle className="h-5 w-5 shrink-0 text-[hsl(var(--gold))]" /> The previously agreed plan (v{exp.plan.agreed_version}) has changed. Every co-host must reconfirm the latest terms.
        </p>
      )}
      <MyResponse exp={exp} onUpdate={onUpdate} />
      {lead && !["closed", "cancelled"].includes(exp.status) ? (
        <>
          {sent && <p className="text-sm text-muted-foreground">Version {exp.plan.plan_version} is out for agreement. Saving any change to amounts, budget or guest terms creates a new version and resets every confirmation.</p>}
          <ContributionEditor exp={exp} draft={draft} setDraft={setDraft} />
          <div className="hairline" />
          <h3 className="font-display text-2xl">Current status by co-host</h3>
          <ContributionTable plan={exp.plan} />
        </>
      ) : <ContributionTable plan={exp.plan} />}
    </div>
  );
};
